import { motion } from 'framer-motion';
import { Crown, Smile, Flame } from 'lucide-react';

import { Card, cn } from '@llura/ui';
import { FlowSteps } from '@llura/constants';

import { TONE_ACCENT } from '../../../lib/coach-persona';
import type { CoachTone } from '../../../stores/coach.store';
import { useStepTiming } from '../../../lib/analytics';
import { useStrings } from '../../../hooks/useStrings';

const TONES: { id: CoachTone; icon: typeof Crown }[] = [
  { id: 'dominant', icon: Crown },
  { id: 'playful', icon: Smile },
  { id: 'sensual', icon: Flame },
];

interface ToneStepProps {
  value: CoachTone | null;
  onSelect: (tone: CoachTone) => void;
}

const ACCENT_RING: Record<string, string> = {
  primary: 'border-primary glow-primary',
  secondary: 'border-secondary glow-secondary',
  accent: 'border-accent glow-accent',
};

const ACCENT_ICON: Record<string, string> = {
  primary: 'bg-primary text-primary-foreground',
  secondary: 'bg-secondary text-secondary-foreground',
  accent: 'bg-accent text-accent-foreground',
};

export function ToneStep({ value, onSelect }: ToneStepProps) {
  useStepTiming(FlowSteps.CoachConfiguratorTone);
  const { strings, interp } = useStrings();
  const stepLabel = interp(strings.onboarding.stepLabel, {
    current: 2,
    total: strings.onboarding.totalSteps,
  });
  return (
    <motion.div
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -24 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="space-y-6"
    >
      <header className="space-y-2 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">{stepLabel}</p>
        <h2 className="font-serif text-3xl">{strings.onboarding.tone.title}</h2>
        <p className="text-sm text-muted-foreground">
          {strings.onboarding.tone.hint}
        </p>
      </header>

      <div className="grid gap-3">
        {TONES.map(({ id, icon: Icon }) => {
          const meta = strings.coach.tones[id];
          const accent = TONE_ACCENT[id];
          const active = value === id;
          return (
            <Card
              key={id}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(id)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect(id)}
              className={cn(
                'flex cursor-pointer items-center gap-4 border-border bg-card p-5 transition-all hover:border-primary/60',
                active && ACCENT_RING[accent],
              )}
            >
              <div
                className={cn(
                  'flex h-12 w-12 items-center justify-center rounded-full bg-muted text-foreground',
                  active && ACCENT_ICON[accent],
                )}
              >
                <Icon className="h-6 w-6" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-medium">{meta.label}</div>
                <div className="text-xs text-muted-foreground">{meta.tagline}</div>
              </div>
            </Card>
          );
        })}
      </div>
    </motion.div>
  );
}
